import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export interface SlotExclusion {
  id?: string;
  slot_id?: string;
  menu_item_id: string;
}

export interface CategorySlot {
  id?: string;
  category_id: string;
  quantity: number;
  sort_order: number;
  label_override?: string | null;
  category?: { id: string; name: string };
  exclusions?: SlotExclusion[];
}

export interface PackageRow {
  id: string;
  name: string;
  description: string | null;
  pricing_type: string;
  price: number;
  min_guests: number | null;
  max_guests: number | null;
  is_active: boolean;
  selection_mode: string;
  slug: string | null;
  is_published: boolean;
  cover_image_url: string | null;
  cta_text: string;
  show_prices: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  items?: { id: string; menu_item_id: string; quantity: number; notes: string | null; menu_item?: { id: string; name: string } }[];
  event_types?: { id: string; event_type_id: string; event_type?: { id: string; name: string } }[];
  category_slots?: CategorySlot[];
  created_by_profile?: { display_name: string | null } | null;
  updated_by_profile?: { display_name: string | null } | null;
}

export function usePackages() {
  return useQuery({
    queryKey: ["packages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("packages")
        .select("*, items:package_items(*, menu_item:menu_items(id, name)), event_types:package_event_types(*, event_type:event_types(id, name)), category_slots:package_category_slots(*, category:menu_categories(id, name), exclusions:package_slot_exclusions(id, slot_id, menu_item_id)), created_by_profile:profiles!packages_created_by_fkey(display_name), updated_by_profile:profiles!packages_updated_by_fkey(display_name)")
        .order("name");
      if (error) throw error;
      return data as PackageRow[];
    },
  });
}

export function useEventTypes() {
  return useQuery({
    queryKey: ["event_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_types")
        .select("*")
        .order("name");
      if (error) throw error;
      return data as { id: string; name: string; description: string | null; is_active: boolean; created_at: string }[];
    },
  });
}

export function useCreateEventType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: { name: string; description?: string }) => {
      const { data, error } = await supabase.from("event_types").insert(values).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event_types"] });
      toast.success("Event type created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateEventType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...values }: { id: string; name?: string; description?: string; is_active?: boolean }) => {
      const { error } = await supabase.from("event_types").update(values).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event_types"] });
      qc.invalidateQueries({ queryKey: ["packages"] });
      toast.success("Event type updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeleteEventType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Remove from package_event_types first
      const { error: linkError } = await supabase.from("package_event_types").delete().eq("event_type_id", id);
      if (linkError) throw linkError;
      const { error } = await supabase.from("event_types").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event_types"] });
      qc.invalidateQueries({ queryKey: ["packages"] });
      toast.success("Event type deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export interface CreatePackageInput {
  name: string;
  description?: string;
  pricing_type: string;
  price: number;
  min_guests?: number;
  max_guests?: number;
  is_active?: boolean;
  selection_mode?: string;
  item_ids?: { menu_item_id: string; quantity: number; notes?: string }[];
  event_type_ids?: string[];
  category_slots?: { category_id: string; quantity: number; sort_order: number; label_override?: string; exclusions?: { menu_item_id: string }[] }[];
  // Publishing fields
  slug?: string;
  is_published?: boolean;
  cover_image_url?: string | null;
  cta_text?: string;
  show_prices?: boolean;
}

// Child writes run after the package row is saved, so a failure leaves the package only
// partly saved. Throw on it, naming the part, so the save reports an error (#645).
async function childWrite<T>(part: string, write: PromiseLike<{ data: T | null; error: { message: string } | null }>) {
  const { data, error } = await write;
  if (error) throw new Error(`The package was saved, but its ${part} weren't: ${error.message}`);
  return data;
}

export function useCreatePackage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ item_ids, event_type_ids, category_slots, ...values }: CreatePackageInput) => {
      const { data, error } = await supabase.from("packages").insert({ ...values, created_by: user?.id, updated_by: user?.id }).select().single();
      if (error) throw error;
      if (item_ids?.length) {
        await childWrite("menu items", supabase.from("package_items").insert(item_ids.map((i) => ({ ...i, package_id: data.id }))));
      }
      if (event_type_ids?.length) {
        await childWrite("event types", supabase.from("package_event_types").insert(event_type_ids.map((etId) => ({ package_id: data.id, event_type_id: etId }))));
      }
      if (category_slots?.length) {
        const slotData = (await childWrite("category slots", supabase.from("package_category_slots").insert(category_slots.map((s) => ({ category_id: s.category_id, quantity: s.quantity, sort_order: s.sort_order, label_override: s.label_override, package_id: data.id }))).select())) ?? [];
        // Save exclusions for each slot
        for (let i = 0; i < slotData.length; i++) {
          const exclusions = category_slots[i]?.exclusions;
          if (exclusions?.length) {
            await childWrite("slot exclusions", supabase.from("package_slot_exclusions").insert(
              exclusions.map((ex) => ({ slot_id: slotData[i].id, menu_item_id: ex.menu_item_id }))
            ));
          }
        }
      }
      return data;
    },
    onSuccess: () => {
      toast.success("Package created");
    },
    // Refresh on failure too: a failed child write leaves the package partly saved (#645).
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdatePackage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, item_ids, event_type_ids, category_slots, ...values }: { id: string } & Partial<CreatePackageInput>) => {
      const { error } = await supabase.from("packages").update({ ...values, updated_by: user?.id }).eq("id", id);
      if (error) throw error;
      if (item_ids !== undefined) {
        await childWrite("menu items", supabase.from("package_items").delete().eq("package_id", id));
        if (item_ids.length) {
          await childWrite("menu items", supabase.from("package_items").insert(item_ids.map((i) => ({ ...i, package_id: id }))));
        }
      }
      if (event_type_ids !== undefined) {
        await childWrite("event types", supabase.from("package_event_types").delete().eq("package_id", id));
        if (event_type_ids.length) {
          await childWrite("event types", supabase.from("package_event_types").insert(event_type_ids.map((etId) => ({ package_id: id, event_type_id: etId }))));
        }
      }
      if (category_slots !== undefined) {
        // Delete old exclusions via cascade (deleting slots cascades to exclusions)
        await childWrite("category slots", supabase.from("package_category_slots").delete().eq("package_id", id));
        if (category_slots.length) {
          const slotData = (await childWrite("category slots", supabase.from("package_category_slots").insert(category_slots.map((s) => ({ category_id: s.category_id, quantity: s.quantity, sort_order: s.sort_order, label_override: s.label_override, package_id: id }))).select())) ?? [];
          for (let i = 0; i < slotData.length; i++) {
            const exclusions = category_slots[i]?.exclusions;
            if (exclusions?.length) {
              await childWrite("slot exclusions", supabase.from("package_slot_exclusions").insert(
                exclusions.map((ex) => ({ slot_id: slotData[i].id, menu_item_id: ex.menu_item_id }))
              ));
            }
          }
        }
      }
    },
    onSuccess: () => {
      toast.success("Package updated");
    },
    // Refresh on failure too: a failed child write leaves the package partly saved (#645).
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useTogglePackagePublished() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_published }: { id: string; is_published: boolean }) => {
      const { error } = await supabase.from("packages").update({ is_published, is_active: is_published }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeletePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("packages").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["packages"] });
      toast.success("Package deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDuplicatePackage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (pkg: PackageRow) => {
      const { data, error } = await supabase
        .from("packages")
        .insert({
          name: `${pkg.name} (Copy)`,
          description: pkg.description,
          pricing_type: pkg.pricing_type,
          price: pkg.price,
          min_guests: pkg.min_guests,
          max_guests: pkg.max_guests,
          selection_mode: pkg.selection_mode,
          is_active: false,
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select()
        .single();
      if (error) throw error;
      if (pkg.items?.length) {
        await childWrite("menu items", supabase.from("package_items").insert(
          pkg.items.map((i) => ({ package_id: data.id, menu_item_id: i.menu_item_id, quantity: i.quantity, notes: i.notes }))
        ));
      }
      if (pkg.event_types?.length) {
        await childWrite("event types", supabase.from("package_event_types").insert(
          pkg.event_types.map((et) => ({ package_id: data.id, event_type_id: et.event_type_id }))
        ));
      }
      if (pkg.category_slots?.length) {
        await childWrite("category slots", supabase.from("package_category_slots").insert(
          pkg.category_slots.map((s) => ({ package_id: data.id, category_id: s.category_id, quantity: s.quantity, sort_order: s.sort_order, label_override: s.label_override }))
        ));
      }
      return data;
    },
    onSuccess: () => {
      toast.success("Package duplicated");
    },
    // Refresh on failure too: a failed child write leaves the package partly saved (#645).
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
